/**
 * LinkedInPresenceHome — /linkedin-presence
 *
 * Landing page for LinkedIn Presence Builder (Product #6).
 * Four section cards: Profile Optimization, Content Strategy, Outreach Templates, Analytics.
 */

import {
  Box,
  Button,
  Chip,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import CreateIcon from "@mui/icons-material/Create";
import ForumIcon from "@mui/icons-material/Forum";
import InsightsIcon from "@mui/icons-material/Insights";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  PROFILE_PROMPTS,
  POST_ARCHETYPES,
  OUTREACH_TEMPLATES,
  ANALYTICS_PROMPTS,
} from "../data/linkedinPresenceContent";
import { L } from "../utils/localized";

// coral via theme.palette.primary — per-product accent deferred (spec D2)

interface SectionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  tier: "free" | "all_access" | "hybrid";
  href: string;
  stats?: string;
}

function SectionCard({ icon, title, description, tier, href, stats }: SectionCardProps) {
  const { t } = useTranslation("pages-products");
  const theme = useTheme();
  const navigate = useNavigate();

  const tierChip =
    tier === "free" ? (
      <Chip label={t("LinkedInPresenceHome.freeChipLabel")} size="small" sx={{ bgcolor: alpha(theme.palette.success.main, 0.12), color: theme.palette.success.dark, fontWeight: 500, fontSize: "0.68rem" }} />
    ) : tier === "hybrid" ? (
      <Chip label={t("LinkedInPresenceHome.freePreviewAllAccessChipLabel")} size="small" sx={{ bgcolor: alpha(theme.palette.warning.main, 0.12), color: theme.palette.warning.main, fontWeight: 500, fontSize: "0.68rem" }} />
    ) : (
      <Chip icon={<LockIcon sx={{ fontSize: "0.85rem !important" }} />} label={t("LinkedInPresenceHome.allAccessChipLabel")} size="small" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main, fontWeight: 500, fontSize: "0.68rem" }} />
    );

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 3,
        p: 3,
        height: "100%",
        cursor: "pointer",
        transition: "all 0.2s",
        "&:hover": {
          borderColor: alpha(theme.palette.primary.main, 0.4),
          transform: "translateY(-2px)",
          boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
        },
      }}
      onClick={() => navigate(href)}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {icon}
        </Box>
        {tierChip}
      </Stack>
      <Typography variant="h6" fontWeight={500} gutterBottom>{title}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65, mb: stats ? 1.5 : 0 }}>{description}</Typography>
      {stats && (
        <Typography variant="caption" color="text.disabled" sx={{ fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
          {stats}
        </Typography>
      )}
    </Paper>
  );
}

export default function LinkedInPresenceHome() {
  const { t, i18n } = useTranslation("pages-products");
  const theme = useTheme();
  const navigate = useNavigate();
  const profilePrompts = L(PROFILE_PROMPTS, i18n.language);
  const postArchetypes = L(POST_ARCHETYPES, i18n.language);
  const outreachTemplates = L(OUTREACH_TEMPLATES, i18n.language);
  const analyticsPrompts = L(ANALYTICS_PROMPTS, i18n.language);

  const whyItsDifferent = [
    {
      title: t("LinkedInPresenceHome.whyItem1Title"),
      body: t("LinkedInPresenceHome.whyItem1Body"),
    },
    {
      title: t("LinkedInPresenceHome.whyItem2Title"),
      body: t("LinkedInPresenceHome.whyItem2Body"),
    },
    {
      title: t("LinkedInPresenceHome.whyItem3Title"),
      body: t("LinkedInPresenceHome.whyItem3Body"),
    },
  ];

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", pb: 10 }}>
      {/* Hero */}
      <Box sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`, py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems="flex-start">
            <Chip label={t("LinkedInPresenceHome.heroEyebrow")} size="small" sx={{ fontWeight: 500, fontSize: "0.72rem", letterSpacing: "0.08em", bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main }} />
            <Typography variant="h2" fontWeight={500} sx={{ letterSpacing: "-0.03em", fontSize: { xs: "2.4rem", md: "3.2rem" }, maxWidth: 900 }}>
              {t("LinkedInPresenceHome.heroHeadlineLine1")}
              <br />
              <Box component="span" sx={{ color: theme.palette.primary.main }}>{t("LinkedInPresenceHome.heroHeadlineLine2")}</Box>
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, maxWidth: 780, lineHeight: 1.5 }}>
              {t("LinkedInPresenceHome.heroSubheading")}
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} mt={2}>
              <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={() => navigate("/linkedin-presence/profile")} sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, px: 3, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}>
                {t("LinkedInPresenceHome.startHeadlineRewriterButton")}
              </Button>
              <Button variant="outlined" size="large" onClick={() => navigate("/linkedin-presence/content")} sx={{ fontWeight: 500, px: 3, borderColor: alpha(theme.palette.primary.main, 0.5), color: theme.palette.primary.main, "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                {t("LinkedInPresenceHome.seePostExemplarsButton")}
              </Button>
            </Stack>
            <Stack direction="row" spacing={3} mt={3} flexWrap="wrap" gap={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                <Typography variant="body2" color="text.secondary">{t("LinkedInPresenceHome.checkHeadlineFree")}</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                <Typography variant="body2" color="text.secondary">{t("LinkedInPresenceHome.checkNewsCommentaryFree")}</Typography>
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center">
                <CheckCircleIcon sx={{ fontSize: 18, color: theme.palette.success.main }} />
                <Typography variant="body2" color="text.secondary">{t("LinkedInPresenceHome.checkExemplarPostsIncluded")}</Typography>
              </Stack>
            </Stack>
          </Stack>
        </Container>
      </Box>

      {/* What's inside */}
      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Stack spacing={1} mb={5}>
          <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em", textTransform: "uppercase" }}>{t("LinkedInPresenceHome.whatsInsideEyebrow")}</Typography>
          <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em" }}>{t("LinkedInPresenceHome.whatsInsideTitle")}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 780 }}>
            {t("LinkedInPresenceHome.whatsInsideBody")}
          </Typography>
        </Stack>

        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(2, 1fr)" }, gap: 2.5 }}>
          <SectionCard
            icon={<PersonIcon />}
            title={t("LinkedInPresenceHome.profileCardTitle")}
            description={t("LinkedInPresenceHome.profileCardDescription")}
            tier="hybrid"
            href="/linkedin-presence/profile"
            stats={t("LinkedInPresenceHome.profileCardStats", { count: profilePrompts.length })}
          />
          <SectionCard
            icon={<CreateIcon />}
            title={t("LinkedInPresenceHome.contentCardTitle")}
            description={t("LinkedInPresenceHome.contentCardDescription")}
            tier="hybrid"
            href="/linkedin-presence/content"
            stats={t("LinkedInPresenceHome.contentCardStats", { count: postArchetypes.length })}
          />
          <SectionCard
            icon={<ForumIcon />}
            title={t("LinkedInPresenceHome.outreachCardTitle")}
            description={t("LinkedInPresenceHome.outreachCardDescription")}
            tier="all_access"
            href="/linkedin-presence/outreach"
            stats={t("LinkedInPresenceHome.outreachCardStats", { count: outreachTemplates.length })}
          />
          <SectionCard
            icon={<InsightsIcon />}
            title={t("LinkedInPresenceHome.analyticsCardTitle")}
            description={t("LinkedInPresenceHome.analyticsCardDescription")}
            tier="all_access"
            href="/linkedin-presence/profile"
            stats={t("LinkedInPresenceHome.analyticsCardStats", { count: analyticsPrompts.length })}
          />
        </Box>
      </Container>

      {/* Why it's different */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03), borderY: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`, py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Stack spacing={1} mb={4}>
            <Typography variant="caption" fontWeight={700} sx={{ color: theme.palette.primary.main, letterSpacing: "0.08em", textTransform: "uppercase" }}>{t("LinkedInPresenceHome.whyDifferentEyebrow")}</Typography>
            <Typography variant="h4" fontWeight={500} sx={{ letterSpacing: "-0.02em" }}>{t("LinkedInPresenceHome.whyDifferentTitle")}</Typography>
          </Stack>

          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 3 }}>
            {whyItsDifferent.map((item, i) => (
              <Paper key={i} elevation={0} sx={{ bgcolor: "background.paper", border: `1px solid ${theme.palette.divider}`, borderRadius: 3, p: 3 }}>
                <Typography variant="h6" fontWeight={500} gutterBottom>{item.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>{item.body}</Typography>
              </Paper>
            ))}
          </Box>
        </Container>
      </Box>

      {/* Final CTA */}
      <Container maxWidth="md" sx={{ py: { xs: 6, md: 8 } }}>
        <Paper elevation={0} sx={{ textAlign: "center", p: { xs: 4, md: 6 }, background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`, borderRadius: 3 }}>
          <Typography variant="h4" fontWeight={500} gutterBottom sx={{ letterSpacing: "-0.02em" }}>{t("LinkedInPresenceHome.finalCtaTitle")}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 520, mx: "auto", mb: 4 }}>
            {t("LinkedInPresenceHome.finalCtaBody")}
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="center">
            <Button variant="contained" size="large" endIcon={<ArrowForwardIcon />} onClick={() => navigate("/linkedin-presence/profile")} sx={{ bgcolor: theme.palette.primary.main, fontWeight: 500, px: 3, "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.85) } }}>
              {t("LinkedInPresenceHome.runHeadlineRewriterButton")}
            </Button>
            <Button variant="text" size="large" onClick={() => navigate("/pricing")} sx={{ color: theme.palette.primary.main, fontWeight: 500, px: 3 }}>
              {t("LinkedInPresenceHome.seeAllAccessPricingButton")}
            </Button>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
